import { IconButton, InputAdornment, Typography } from '@material-ui/core';
import { Delete } from '@material-ui/icons';
import { Column, Columns, Stack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { FieldArray, useField } from 'formik';
import { GeoField } from 'shared/geo/GeoField';
import { Geocoding } from 'shared/geo/GeoHelpers';
import {
  mapGeocodeToVenue,
  mapVenueToGeocode,
  PostingSearchCriteriaVenueDTO,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { joinStrings } from 'shared/utils/StringUtils';
import styled from 'styled-components';

function formatNumberOrder(value: number) {
  switch (value) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

function formatOptionLabel({
  postcode,
  place,
  region_short,
  locality,
  country,
}: Geocoding) {
  return joinStrings(
    ', ',
    locality,
    place,
    joinStrings(' ', region_short, postcode),
    country === 'United States' ? 'USA' : country,
  );
}

const Label = styled(Typography)`
  line-height: 1;
`;

const Caption = styled(Typography)`
  line-height: 1;
  font-size: 10px;
`;

interface LATRWaypointFieldProps {
  name: string;
  index: number;
}

function LATRWaypointField({ name, index, ...rest }: LATRWaypointFieldProps) {
  const [{ value }, _, { setValue }] =
    useField<PostingSearchCriteriaVenueDTO>(name);
  return (
    <GeoField
      {...rest}
      fullWidth={true}
      value={!value ? undefined : mapVenueToGeocode(value)}
      types={['place', 'postcode', 'locality']}
      formatOptionLabel={formatOptionLabel}
      onChange={(updatedValue) => {
        void setValue(!updatedValue ? {} : mapGeocodeToVenue(updatedValue));
      }}
      TextFieldProps={{
        name,
        fullWidth: true,
        placeholder: 'Pickup Cities or ZIP',
        InputProps: {
          startAdornment: (
            <InputAdornment position="start">
              <Label variant="body1" color="textSecondary" align="center">
                {index}
                <sup>
                  <Caption variant="caption">
                    {formatNumberOrder(index)}
                  </Caption>
                </sup>
              </Label>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

export function LATRWaypointsField({ name }: { name: string }) {
  const [{ value = [] }] = useField<
    PostingSearchCriteriaVenueDTO[] | undefined
  >(name);

  return (
    <FieldArray
      name={name}
      render={({ push, remove }) => (
        <Stack space="xsmall">
          {value.length > 0 && (
            <Typography variant="h6" color="textSecondary">
              Waypoints
            </Typography>
          )}

          <Stack space="small">
            {value.map((_, index) => (
              <Columns key={index} align="center" space="small">
                <Column>
                  <LATRWaypointField
                    key={index}
                    index={index + 1}
                    name={`${name}.${index}`}
                  />
                </Column>

                <Column width="content">
                  <IconButton size="small" onClick={() => remove(index)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Column>
              </Columns>
            ))}

            {value.length < 3 && (
              <Button
                variant="text"
                onClick={() => {
                  push({});
                }}
              >
                + Add Route Waypoint
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    />
  );
}
